// Creator Studio uploads: direct browser → R2 through presigned URLs, so large
// files never pass through the Worker. Exposed as window.AHStudio and used by
// the studio page's upload form (the app bundle).
//
//   1. POST /api/studio/upload-url       → videoId + presigned PUT URLs
//   2. PUT thumbnail, then video, via XHR → real byte-level progress; cancel() aborts
//   3. POST /api/studio/upload-complete  → server verifies the files; status 'processing' (awaiting review)
//   On failure or cancel: POST /api/studio/upload-cancel removes the partial upload.

import { getSupabase } from './auth';

import { mediaLimit, mediaLimitMessage, fits1080p } from './media-policy';
export { MAX_VIDEO_BYTES as MAX_MEDIA_BYTES } from './media-policy';
export const MEDIA_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'audio/mpeg', 'audio/mp4'];

export interface UploadOptions {
  fields: FormData; // title, description, category, tags, consent
  media: File;
  thumbnail: File;
  membersOnly: boolean;
  minimumTier: 'supporter' | 'vip' | string;
  commentsHidden?: boolean;
  onProgress?: (percent: number) => void;
  onStatus?: (message: string) => void;
}

interface PrepareResponse {
  videoId: string;
  uploadUrl: string;
  thumbnailUploadUrl: string;
  uploadHeaders: Record<string, string>;
  thumbnailUploadHeaders: Record<string, string>;
  publicMediaUrl: string;
}

let active: { xhr: XMLHttpRequest | null; cancelled: boolean; videoId: string | null } | null = null;

const formatBytes = (n: number) => (n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} GB` : `${Math.max(0.1, n / 1024 ** 2).toFixed(1)} MB`);

async function api<T>(path: string, body: unknown): Promise<T> {
  const { data } = (await getSupabase()?.auth.getSession()) ?? { data: { session: null } };
  const token = data.session?.access_token;
  if (!token) throw new Error('Please sign in to upload.');
  const res = await fetch(path, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || `Request failed (${res.status}).`);
  return payload as T;
}

/** PUT a file to a presigned URL with progress. Rejects with 'AbortError' when cancelled. */
function put(url: string, file: File, headers: Record<string, string>, onLoaded: (bytes: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (active) active.xhr = xhr;
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => onLoaded(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      reject(new Error(xhr.status === 403
        ? 'The upload link expired or the file didn’t match what was declared. Please try again.'
        : `Upload failed (${xhr.status}). Please try again.`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload. Check your connection and try again.'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled.', 'AbortError'));
    xhr.send(file);
  });
}

/** Reads the duration from the file's metadata in the browser (null if it can't). */
function probeDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const el = document.createElement(file.type.startsWith('audio/') ? 'audio' : 'video');
    const url = URL.createObjectURL(file);
    const done = (value: number | null) => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      el.removeAttribute('src');
      resolve(value);
    };
    const timer = setTimeout(() => done(null), 8000);
    el.preload = 'metadata';
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? el.duration : null);
    el.onerror = () => done(null);
    el.src = url;
  });
}

export async function checkVideoResolution(file: File): Promise<void> {
  if (!file.type.startsWith('video/')) return;
  await new Promise<void>((resolve, reject) => {
    const el = document.createElement('video'), url = URL.createObjectURL(file);
    const done = (error?: string) => {
      clearTimeout(timer); el.onloadedmetadata = null; el.onerror = null;
      el.removeAttribute('src'); el.load(); URL.revokeObjectURL(url);
      error ? reject(new Error(error)) : resolve();
    };
    const timer = setTimeout(() => done('Could not read video dimensions. Try an H.264/AAC MP4.'), 10_000);
    el.preload = 'metadata';
    el.onloadedmetadata = () => done(fits1080p(el.videoWidth, el.videoHeight) ? undefined : 'Video must be 1080p or smaller (1920 × 1080, or portrait equivalent).');
    el.onerror = () => done('Could not read video dimensions. Try an H.264/AAC MP4.');
    el.src = url;
  });
}

export async function upload(opts: UploadOptions): Promise<{ videoId: string }> {
  if (active) throw new Error('An upload is already in progress.');
  const { media, thumbnail, fields } = opts;
  if (!MEDIA_TYPES.includes(media.type)) throw new Error('Upload an MP4, WebM, or MOV video (or an MP3 / M4A audio file).');
  if (media.size > mediaLimit(media.type)) throw new Error(mediaLimitMessage(media.type));

  const progress = (p: number) => opts.onProgress?.(Math.round(p));
  const status = (m: string) => opts.onStatus?.(m);
  const warnOnLeave = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
  const state = { xhr: null as XMLHttpRequest | null, cancelled: false, videoId: null as string | null };
  active = state;
  addEventListener('beforeunload', warnOnLeave);

  try {
    status('Preparing your upload…');
    progress(2);
    await checkVideoResolution(media);
    if (state.cancelled) throw new DOMException('Upload cancelled.', 'AbortError');
    const duration = probeDuration(media);
    const prep = await api<PrepareResponse>('/api/studio/upload-url', {
      title: fields.get('title'),
      description: fields.get('description'),
      category: fields.get('category'),
      tags: String(fields.get('tags') ?? '').split(',').map((t) => t.trim()).filter(Boolean),
      visibility: fields.get('visibility') || 'public',
      membersOnly: opts.membersOnly,
      minimumTier: opts.minimumTier,
      commentsHidden: opts.commentsHidden === true,
      mediaType: media.type,
      mediaSize: media.size,
      thumbnailType: thumbnail.type,
      thumbnailSize: thumbnail.size,
      consent: fields.get('consent') === 'on',
    });
    state.videoId = prep.videoId;
    if (state.cancelled) throw new DOMException('Upload cancelled.', 'AbortError');

    // 3% → 97% tracks bytes sent across both files; the thumbnail goes first.
    const total = thumbnail.size + media.size;
    let lastStatus = 0;
    const report = (sent: number) => {
      progress(3 + (sent / total) * 94);
      if (Date.now() - lastStatus > 250 || sent === total) {
        lastStatus = Date.now();
        status(`Uploading… ${Math.floor((sent / total) * 100)}% (${formatBytes(sent)} of ${formatBytes(total)})`);
      }
    };
    await put(prep.thumbnailUploadUrl, thumbnail, prep.thumbnailUploadHeaders, (b) => report(b));
    await put(prep.uploadUrl, media, prep.uploadHeaders, (b) => report(thumbnail.size + b));

    status('Finishing up…');
    progress(98);
    await api('/api/studio/upload-complete', { videoId: prep.videoId, durationSeconds: await duration });
    progress(100);
    return { videoId: prep.videoId };
  } catch (err) {
    if (state.videoId) await api('/api/studio/upload-cancel', { videoId: state.videoId }).catch(() => {});
    if (state.cancelled || (err as Error).name === 'AbortError') throw new Error('Upload cancelled. Nothing was published.');
    throw err;
  } finally {
    removeEventListener('beforeunload', warnOnLeave);
    active = null;
  }
}

/** Aborts the upload in progress; upload() then rejects and cleans up. */
export function cancel() {
  if (!active) return;
  active.cancelled = true;
  active.xhr?.abort();
}

export const isUploading = () => active !== null;
